/**
 * Configuración de la autenticación con Keycloak, compartida por los frontends.
 *
 * Aquí no hay pantalla de login ni se maneja ninguna contraseña: eso vive en
 * Keycloak. Esto sólo describe CÓMO hablar con él —flujo de código con PKCE, sin
 * secreto de cliente— para que el backoffice y el POS lo hagan igual.
 *
 * Ver `docs/AUTENTICACION.md` para el porqué de cada decisión.
 */
export interface AuthConfig {
  /** Emisor, p. ej. `https://login.farmacentercerete.com/realms/farmacenter`. */
  authority: string;
  /** `farmacenter-erp-frontend` o `farmacenter-pos-frontend`. */
  clientId: string;
  /** A dónde vuelve Keycloak con el código. En Tauri, `tauri://localhost`. */
  redirectUri: string;
  /**
   * Los permisos que esta aplicación pide. `openid` es obligatorio; el resto son
   * alcances del contrato asignados al cliente en Keycloak. El backoffice, por ser
   * la herramienta de administración, pide el conjunto completo ({@link TODOS_LOS_SCOPES});
   * un cliente más acotado (p. ej. el POS) declara solo los suyos.
   */
  scopes: string[];
}

/** El alcance base de OIDC, siempre presente. */
export const OPENID_SCOPE = "openid";

/**
 * Los alcances de sólo lectura que el backoffice necesita para arrancar: mostrar
 * el catálogo, las existencias y la organización. Las pantallas de escritura piden
 * los suyos cuando el usuario entra en ellas.
 */
export const BACKOFFICE_READ_SCOPES = [
  OPENID_SCOPE,
  // Todos verificados contra el contrato. Los `organization.*` se piden sin
  // problema: se cerró de raíz el bug de Keycloak 26 (la función Organizations,
  // que reservaba el namespace y rechazaba dos scopes `organization.*` juntos, está
  // desactivada, y el realm usa un flujo de login sin el paso `organization`).
  "catalog.product.read",
  "inventory.availability.read",
  "inventory.lot.read",
  "organization.tenant.read",
  "organization.legal_entity.read",
  "organization.topology.read",
  // Listados operativos que consumen las pantallas del backoffice. Todos
  // verificados contra el contrato (operaciones GET de solo lectura).
  "procurement.invoice.read",
  "procurement.purchase_order.read",
  "procurement.receipt.read",
  "sales.sale.read",
  "accounting.period.read",
  "accounting.journal.read",
  "payments.sale_payment.read",
  "cash.session.read",
  "cash.receipt.read",
  "cash.disbursement.read",
] as const;

/**
 * Los alcances de ESCRITURA del backoffice: dar de alta la estructura de la
 * organización (Configuración). El de `bootstrap` es el que permite arrancar —
 * crear la primera entidad legal, sede, etc. bajo un tenant recién creado sin un
 * permiso de contexto previo—; es un privilegio fuerte, pensado para el montaje
 * inicial. Cuando el sistema esté poblado conviene restringirlo a quien monta.
 */
export const BACKOFFICE_WRITE_SCOPES = [
  // Organización
  "organization.tenant.write",
  "organization.legal_entity.write",
  "organization.topology.write",
  "organization.authorization.bootstrap",
  "organization.authorization.write",
  // Catálogo y terceros
  "catalog.product.write",
  "catalog.third_party.write",
  // Fiscal, precios y contabilidad maestra
  "tax.rule.write",
  // Nota: los precios se gestionan con pricing.rule.write; no existe un
  // pricing.price.write asignado al cliente (pedirlo rompe el login con
  // "Invalid scopes"). Listas y reglas de precio van por pricing.rule.write.
  "pricing.rule.write",
  "accounting.master.write",
  "accounting.period.write",
  // Políticas operativas
  "procurement.receipt_policy.write",
  "procurement.invoice_match_policy.write",
  "procurement.replenishment_policy.write",
  "sales.sale_policy.write",
  "sales.sale_return_policy.write",
  "cash.disbursement_policy.write",
  "cash.withdrawal_policy.write",
  "payments.payment_policy.write",
  "inventory.presentation_conversion_policy.write",
  // Documentos electrónicos (simulador DIAN)
  "electronic_documents.admin.write",
] as const;

/**
 * TODOS los alcances del sistema: los 118 que el cliente `farmacenter-erp-frontend`
 * tiene asignados en Keycloak (como opcionales), más `openid`. El backoffice es la
 * herramienta de administración y su usuario admin debe poder con todo, así que
 * pide el conjunto completo, no un subconjunto.
 *
 * Importante: el scope NO es la autorización real. Es solo la capacidad declarada
 * en el token; el permiso efectivo se aplica en el servidor, por contexto
 * (tenant/entidad/sede) y por RBAC. Por eso cargar el token con todo es seguro:
 * el backend sigue siendo el que decide.
 *
 * Lista generada desde el contrato y contrastada con los client-scopes de Keycloak
 * (todos válidos: pedir uno no asignado rompería el login con "Invalid scopes").
 * Si el backend añade un scope nuevo, asígnalo al cliente y añádelo aquí —o, mejor,
 * pásalo a "default" en el cliente de Keycloak y entrará en el token sin listarlo—.
 */
export const TODOS_LOS_SCOPES = [
  OPENID_SCOPE,
  "accounting.journal.post",
  "accounting.journal.read",
  "accounting.journal.reverse",
  "accounting.journal.substitute",
  "accounting.master.read",
  "accounting.master.write",
  "accounting.period.read",
  "accounting.period.reopen",
  "accounting.period.write",
  "accounting.proposal.read",
  "cash.close_adjustment.write",
  "cash.count.read",
  "cash.count.write",
  "cash.disbursement.read",
  "cash.disbursement.reverse",
  "cash.disbursement.write",
  "cash.disbursement_policy.read",
  "cash.disbursement_policy.write",
  "cash.receipt.read",
  "cash.receipt.reverse",
  "cash.receipt.write",
  "cash.session.close",
  "cash.session.open",
  "cash.session.read",
  "cash.withdrawal.read",
  "cash.withdrawal.write",
  "cash.withdrawal_policy.read",
  "cash.withdrawal_policy.write",
  "catalog.product.read",
  "catalog.product.write",
  "catalog.third_party.read",
  "catalog.third_party.write",
  "electronic_documents.admin.read",
  "electronic_documents.admin.write",
  "integrations.procurement_import.publish",
  "integrations.procurement_import.read",
  "integrations.procurement_import.write",
  "inventory.adjustment.approve",
  "inventory.adjustment.read",
  "inventory.adjustment.write",
  "inventory.availability.read",
  "inventory.kardex.read",
  "inventory.kardex.write",
  "inventory.lot.read",
  "inventory.lot.write",
  "inventory.presentation_conversion.read",
  "inventory.presentation_conversion.write",
  "inventory.presentation_conversion_policy.read",
  "inventory.presentation_conversion_policy.write",
  "inventory.reconciliation.read",
  "inventory.reconciliation.write",
  "inventory.reservation.write",
  "inventory.transfer.approve",
  "inventory.transfer.dispatch",
  "inventory.transfer.read",
  "inventory.transfer.receive",
  "inventory.transfer.write",
  "organization.authorization.bootstrap",
  "organization.authorization.read",
  "organization.authorization.write",
  "organization.legal_entity.read",
  "organization.legal_entity.write",
  "organization.tenant.read",
  "organization.tenant.write",
  "organization.topology.read",
  "organization.topology.write",
  "payments.payment_policy.read",
  "payments.payment_policy.write",
  "payments.sale_payment.read",
  "payments.sale_payment.write",
  "pricing.price.read",
  "pricing.rule.write",
  "procurement.debit_note.read",
  "procurement.debit_note.write",
  "procurement.invoice.read",
  "procurement.invoice.write",
  "procurement.invoice_match_policy.read",
  "procurement.invoice_match_policy.write",
  "procurement.purchase_order.issue",
  "procurement.purchase_order.read",
  "procurement.purchase_order.write",
  "procurement.receipt.read",
  "procurement.receipt.write",
  "procurement.receipt_policy.read",
  "procurement.receipt_policy.write",
  "procurement.replenishment.convert",
  "procurement.replenishment.read",
  "procurement.replenishment.write",
  "procurement.replenishment_policy.read",
  "procurement.replenishment_policy.write",
  "procurement.requisition.approve",
  "procurement.requisition.cancel",
  "procurement.requisition.read",
  "procurement.requisition.submit",
  "procurement.requisition.write",
  "procurement.return.read",
  "procurement.return.write",
  "sales.customer.read",
  "sales.customer.write",
  "sales.delivery_order.cancel",
  "sales.delivery_order.deliver",
  "sales.delivery_order.dispatch",
  "sales.delivery_order.pick",
  "sales.delivery_order.read",
  "sales.delivery_order.write",
  "sales.quotation.convert",
  "sales.quotation.read",
  "sales.quotation.write",
  "sales.sale.read",
  "sales.sale.write",
  "sales.sale_policy.read",
  "sales.sale_policy.write",
  "sales.sale_return.read",
  "sales.sale_return.write",
  "sales.sale_return_policy.read",
  "sales.sale_return_policy.write",
  "tax.rule.read",
  "tax.rule.write",
] as const;

/**
 * Configuración de auth del backoffice. Por defecto pide TODOS los alcances (es la
 * herramienta de administración); un consumidor puede acotar pasando `scopes`.
 */
export function buildAuthConfig(overrides: Partial<AuthConfig> & Pick<AuthConfig, "authority" | "clientId" | "redirectUri">): AuthConfig {
  return {
    scopes: [...TODOS_LOS_SCOPES],
    ...overrides,
  };
}
